"use client";

import { Mail, MailPlus } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { NewsletterSubscribeForm } from "./newsletter-subscribe-form";
import { useNewsletterSubscribe } from "./use-newsletter-subscribe";

export interface SubscribeDialogProps {
  triggerVariant?: "icon" | "primary";
  triggerClassName?: string;
  /** Open the dialog immediately on mount */
  defaultOpen?: boolean;
  /** Custom trigger element — when provided, replaces the built-in trigger button */
  children?: ReactNode;
}

export function SubscribeDialog({
  triggerVariant = "icon",
  triggerClassName,
  defaultOpen = false,
  children,
}: SubscribeDialogProps) {
  const [open, setOpen] = useState(defaultOpen);
  const {
    email,
    setEmail,
    subscribe,
    reportType,
    isSubmitting,
    isError,
    isSuccess,
    state,
    reset,
  } = useNewsletterSubscribe({ source: "header_dialog" });

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      reset();
    }
  };

  const trigger =
    children ??
    (triggerVariant === "primary" ? (
      <Button className={cn("gap-2", triggerClassName)} type="button">
        <Mail className="h-4 w-4" />
        <span>Subscribe to Daily Email</span>
      </Button>
    ) : (
      <Button
        aria-label="Subscribe to Daily Email"
        className={cn("h-9 w-9 text-fd-muted-foreground", triggerClassName)}
        size="icon"
        type="button"
        variant="ghost"
      >
        <MailPlus className="h-4 w-4" />
      </Button>
    ));

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-sm" showCloseButton={false}>
        <NewsletterSubscribeForm
          alreadySubscribed={state.alreadySubscribed}
          currentEdition={reportType}
          email={email}
          errorMessage={state.message}
          isError={isError}
          isSubmitting={isSubmitting}
          isSuccess={isSuccess}
          onEmailChange={setEmail}
          onSubscribe={subscribe}
        />
      </DialogContent>
    </Dialog>
  );
}
